import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
  type QueryClient,
} from "@tanstack/react-query";
import {
  Archive,
  Download,
  Eye,
  FileDown,
  FileText,
  History,
  MoreHorizontal,
  Pencil,
  Tags,
  Upload,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  Button,
  cardShellClass,
  ConfirmModal,
  EmptyState,
  FieldError,
  FilterBar,
  fieldErrorId,
  Input,
  Label,
  Modal,
  ModalFooter,
  MultiSelect,
  PageHeader,
  SearchField,
  Select,
  Textarea,
  pageLayoutClass,
  type BadgeVariant,
  type MultiSelectOption,
  type SelectOption,
} from "@/components/ui";
import { buttonVariants } from "@/components/ui/button-variants";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import { FileDropzone, PageFrame } from "@/components/common";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { downloadBlob } from "@/lib/downloadBlob";
import { toastApiError } from "@/lib/errorMessages";
import {
  FILE_SIZE_BYTES,
  formatFileSizeLimit,
  validateFile,
} from "@/lib/fileValidation";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { Permission, ProtocolStatus, ProtocolVersionBump } from "@/types";
import {
  protocolService,
  type Protocol,
  type ProtocolCreatePayload,
  type ProtocolListParams,
  type ProtocolListResponse,
  type ProtocolPublishVersionPayload,
  type ProtocolUpdatePayload,
  type ProtocolVersion,
} from "@/services/protocolService";

const PROTOCOLS_QUERY_KEY = ["admin", "protocols"] as const;
const PROTOCOL_TAGS_QUERY_KEY = [...PROTOCOLS_QUERY_KEY, "tags"] as const;
const PROTOCOL_TEMPLATE_HREF = "/protocols/protocol-template.md";

const PDF_VALIDATION = {
  kinds: ["document"] as const,
  maxSize: FILE_SIZE_BYTES.ATTACHMENT,
  allowedDocumentExtensions: [".pdf"] as const,
};

const PROTOCOL_STATUSES = [
  ProtocolStatus.ACTIVE,
  ProtocolStatus.SUSPENDED,
  ProtocolStatus.OBSOLETE,
] as const;

const PROTOCOL_STATUS_SET = new Set<string>(PROTOCOL_STATUSES);

function nextProtocolVersionLabel(
  current: string,
  bump: ProtocolVersionBump,
): string {
  const match = /^(\d+)\.(\d+)$/.exec(current);
  if (match == null) return current;
  const major = Number(match[1]);
  const minor = Number(match[2]);
  if (bump === ProtocolVersionBump.MAJOR) {
    return `${major + 1}.0`;
  }
  return `${major}.${minor + 1}`;
}

const STATUS_BADGE_VARIANT: Record<ProtocolStatus, BadgeVariant> = {
  [ProtocolStatus.ACTIVE]: "success",
  [ProtocolStatus.SUSPENDED]: "warning",
  [ProtocolStatus.OBSOLETE]: "default",
};

const STATUS_FILTER_DOT_CLASS: Record<ProtocolStatus, string> = {
  [ProtocolStatus.ACTIVE]: "bg-success-500",
  [ProtocolStatus.SUSPENDED]: "bg-warning-500",
  [ProtocolStatus.OBSOLETE]: "bg-surface-400",
};

type StatusFilter = ProtocolStatus | "all";

function parseProtocolStatus(value: string): ProtocolStatus {
  if (PROTOCOL_STATUS_SET.has(value)) {
    return value as ProtocolStatus;
  }
  return ProtocolStatus.ACTIVE;
}

function formatUploadedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

function normalizeCategory(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

function buildListParams(
  statusFilter: StatusFilter,
  categoryFilter: string,
  search: string,
  tagIds: string[],
): ProtocolListParams {
  const params: ProtocolListParams = {};
  if (statusFilter !== "all") {
    params.status = statusFilter;
  }
  const category = normalizeCategory(categoryFilter);
  if (category != null) {
    params.category = category;
  }
  const q = search.trim();
  if (q !== "") {
    params.q = q;
  }
  if (tagIds.length > 0) {
    params.tag_id = tagIds;
  }
  return params;
}

function isProtocolListQuery(query: Query): boolean {
  const key = query.queryKey;
  return (
    key[0] === "admin" &&
    key[1] === "protocols" &&
    (key.length === 2 ||
      (key.length === 3 &&
        key[2] != null &&
        typeof key[2] === "object" &&
        !Array.isArray(key[2])))
  );
}

function listParamsMatchProtocol(
  params: ProtocolListParams,
  protocol: Protocol,
): boolean {
  if (params.status != null && params.status !== protocol.status) {
    return false;
  }
  if (params.category != null) {
    if (
      protocol.category == null ||
      protocol.category.toLowerCase() !== params.category.toLowerCase()
    ) {
      return false;
    }
  }
  const q = params.q?.trim().toLowerCase();
  if (q != null && q !== "" && !protocol.title.toLowerCase().includes(q)) {
    return false;
  }
  if (params.tag_id != null && params.tag_id.length > 0) {
    const selected = new Set(params.tag_id);
    if (!protocol.tags.some((tag) => selected.has(tag.id))) {
      return false;
    }
  }
  return true;
}

/** Keep list caches in sync after create/update/publish (skip version-history keys). */
function syncProtocolInListCaches(
  queryClient: QueryClient,
  protocol: Protocol,
): void {
  for (const query of queryClient
    .getQueryCache()
    .findAll({ queryKey: PROTOCOLS_QUERY_KEY })) {
    if (!isProtocolListQuery(query)) continue;
    const params = (query.queryKey[2] ?? {}) as ProtocolListParams;
    queryClient.setQueryData<ProtocolListResponse>(query.queryKey, (old) => {
      const matches = listParamsMatchProtocol(params, protocol);
      if (old == null) {
        return matches ? { items: [protocol] } : old;
      }
      const index = old.items.findIndex((item) => item.id === protocol.id);
      if (matches) {
        if (index >= 0) {
          const items = old.items.slice();
          items[index] = protocol;
          return { items };
        }
        return { items: [protocol, ...old.items] };
      }
      if (index >= 0) {
        return { items: old.items.filter((item) => item.id !== protocol.id) };
      }
      return old;
    });
  }
}

function ProtocolMetadataFields({
  categoryId,
  category,
  onCategoryChange,
  status,
  onStatusChange,
  statusOptions,
  categoryLabel,
  categoryPlaceholder,
  statusLabel,
}: {
  categoryId: string;
  category: string;
  onCategoryChange: (value: string) => void;
  status: ProtocolStatus;
  onStatusChange: (value: ProtocolStatus) => void;
  statusOptions: SelectOption[];
  categoryLabel: string;
  categoryPlaceholder: string;
  statusLabel: string;
}) {
  return (
    <>
      <div>
        <Label htmlFor={categoryId}>{categoryLabel}</Label>
        <Input
          id={categoryId}
          value={category}
          onChange={(event) => onCategoryChange(event.target.value)}
          placeholder={categoryPlaceholder}
        />
      </div>
      <Select
        label={statusLabel}
        options={statusOptions}
        value={status}
        onChange={(event) =>
          onStatusChange(parseProtocolStatus(event.target.value))
        }
      />
    </>
  );
}

function ProtocolPdfField({
  id,
  file,
  fileError,
  onFilesSelected,
  fileLabel,
  dropzoneTitle,
  dropzoneDescription,
}: {
  id: string;
  file: File | null;
  fileError: string | null;
  onFilesSelected: (files: File[]) => void;
  fileLabel: string;
  dropzoneTitle: string;
  dropzoneDescription: string;
}) {
  return (
    <div>
      <Label as="p">{fileLabel}</Label>
      <FileDropzone
        id={id}
        accept={FILE_ACCEPT.pdfOnly}
        onFilesSelected={onFilesSelected}
        isSelected={file != null}
        title={file?.name ?? dropzoneTitle}
        description={dropzoneDescription}
      />
      {fileError ? (
        <FieldError id={fieldErrorId(id)}>{fileError}</FieldError>
      ) : null}
    </div>
  );
}

const PROTOCOL_TAG_PENDING_PREFIX = "new:";
const PROTOCOL_TAG_VISIBLE_LIMIT = 5;

function pendingTagValue(name: string): string {
  return `${PROTOCOL_TAG_PENDING_PREFIX}${name}`;
}

function isPendingTagValue(value: string): boolean {
  return value.startsWith(PROTOCOL_TAG_PENDING_PREFIX);
}

function pendingTagName(value: string): string {
  return value.slice(PROTOCOL_TAG_PENDING_PREFIX.length);
}

function splitProtocolTagSelection(values: string[]): {
  tagIds: string[];
  tagNames: string[];
} {
  const tagIds: string[] = [];
  const tagNames: string[] = [];
  for (const value of values) {
    if (isPendingTagValue(value)) {
      const name = pendingTagName(value).trim();
      if (name !== "") tagNames.push(name);
    } else {
      tagIds.push(value);
    }
  }
  return { tagIds, tagNames };
}

function ProtocolTagsField({
  id,
  options,
  value,
  onChange,
  label,
  placeholderLabel,
  searchPlaceholderLabel,
  createOptionLabel,
  removeChipLabel,
}: {
  id: string;
  options: readonly MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
  placeholderLabel: string;
  searchPlaceholderLabel: string;
  createOptionLabel: (name: string) => string;
  removeChipLabel: (optionLabel: string) => string;
}) {
  const pendingOptions = useMemo<MultiSelectOption[]>(
    () =>
      value.filter(isPendingTagValue).map((pendingValue) => ({
        value: pendingValue,
        label: pendingTagName(pendingValue),
      })),
    [value],
  );
  const mergedOptions = useMemo(
    () => [...options, ...pendingOptions],
    [options, pendingOptions],
  );

  return (
    <MultiSelect
      id={id}
      label={label}
      options={mergedOptions}
      value={value}
      onChange={onChange}
      placeholderLabel={placeholderLabel}
      searchPlaceholderLabel={searchPlaceholderLabel}
      maxVisibleOptions={PROTOCOL_TAG_VISIBLE_LIMIT}
      createOptionLabel={createOptionLabel}
      onCreateOption={(name) => {
        const pendingValue = pendingTagValue(name);
        if (value.includes(pendingValue)) return;
        const alreadySelected = mergedOptions.some(
          (option) =>
            option.label.toLowerCase() === name.toLowerCase() &&
            value.includes(option.value),
        );
        if (alreadySelected) return;
        const existing = options.find(
          (option) => option.label.toLowerCase() === name.toLowerCase(),
        );
        if (existing) {
          if (!value.includes(existing.value)) {
            onChange([...value, existing.value]);
          }
          return;
        }
        onChange([...value, pendingValue]);
      }}
      removeChipLabel={removeChipLabel}
    />
  );
}

export default function AdminProtocolsPage() {
  const { t, i18n } = useTranslation("adminProtocols");
  const { t: tCommon } = useTranslation("common");
  const { t: tComponents } = useTranslation("components");
  const queryClient = useQueryClient();
  const canWrite = useAuthStore((s) =>
    s.hasPermission(Permission.PROTOCOLS_WRITE),
  );

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    ProtocolStatus.ACTIVE,
  );
  const [categoryFilter, setCategoryFilter] = useState("");
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedCategoryFilter = useDebouncedValue(categoryFilter, 300);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploadStatus, setUploadStatus] = useState<ProtocolStatus>(
    ProtocolStatus.ACTIVE,
  );
  const [uploadTagIds, setUploadTagIds] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<Protocol | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editStatus, setEditStatus] = useState<ProtocolStatus>(
    ProtocolStatus.ACTIVE,
  );
  const [editTagIds, setEditTagIds] = useState<string[]>([]);
  const [editTitleError, setEditTitleError] = useState<string | null>(null);

  const [versionsTarget, setVersionsTarget] = useState<Protocol | null>(null);
  const [publishTarget, setPublishTarget] = useState<Protocol | null>(null);
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionBump, setVersionBump] = useState<ProtocolVersionBump>(
    ProtocolVersionBump.MINOR,
  );
  const [changeSummary, setChangeSummary] = useState("");
  const [versionFileError, setVersionFileError] = useState<string | null>(null);
  const [versionBusyId, setVersionBusyId] = useState<string | null>(null);
  const [obsoleteTarget, setObsoleteTarget] = useState<Protocol | null>(null);

  const [preview, setPreview] = useState<{
    title: string;
    url: string;
  } | null>(null);

  const queryParams = buildListParams(
    statusFilter,
    debouncedCategoryFilter,
    debouncedSearch,
    tagFilters,
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...PROTOCOLS_QUERY_KEY, queryParams],
    queryFn: () => protocolService.list(queryParams),
    placeholderData: keepPreviousData,
  });

  const tagsQuery = useQuery({
    queryKey: PROTOCOL_TAGS_QUERY_KEY,
    queryFn: () => protocolService.listTags(),
  });

  const versionsQuery = useQuery({
    queryKey: [...PROTOCOLS_QUERY_KEY, "versions", versionsTarget?.id],
    queryFn:
      versionsTarget != null
        ? () => protocolService.listVersions(versionsTarget.id)
        : skipToken,
  });

  const items = data?.items ?? [];
  const tagItems = tagsQuery.data?.items ?? [];
  const versionItems = versionsQuery.data?.items ?? [];
  const hasActiveFilters =
    statusFilter !== ProtocolStatus.ACTIVE ||
    categoryFilter.trim() !== "" ||
    tagFilters.length > 0 ||
    search.trim() !== "";
  const emptyMessage =
    canWrite && !hasActiveFilters ? t("emptyWritable") : t("empty");

  const statusOptions = useMemo(
    () =>
      PROTOCOL_STATUSES.map((value) => ({
        value,
        label: t(`status.${value}`),
      })),
    [t],
  );

  const tagOptions = useMemo<MultiSelectOption[]>(
    () => tagItems.map((tag) => ({ value: tag.id, label: tag.name })),
    [tagItems],
  );

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview?.url]);

  function upsertProtocolInListData(
    old: ProtocolListResponse | undefined,
    protocol: Protocol,
  ): ProtocolListResponse {
    if (old == null) return { items: [protocol] };
    const index = old.items.findIndex((item) => item.id === protocol.id);
    if (index >= 0) {
      const items = old.items.slice();
      items[index] = protocol;
      return { items };
    }
    return { items: [protocol, ...old.items] };
  }

  function applyFiltersToShowProtocol(protocol: Protocol) {
    const nextStatus: StatusFilter =
      statusFilter === "all" || statusFilter === protocol.status
        ? statusFilter
        : protocol.status;
    const categoryNorm = normalizeCategory(categoryFilter);
    const nextCategory =
      categoryNorm == null ||
      (protocol.category != null &&
        protocol.category.toLowerCase() === categoryNorm.toLowerCase())
        ? categoryFilter
        : "";
    const q = search.trim().toLowerCase();
    const nextSearch =
      q === "" || protocol.title.toLowerCase().includes(q) ? search : "";
    const nextTags =
      tagFilters.length === 0 ||
      tagFilters.some((tagId) => protocol.tags.some((tag) => tag.id === tagId))
        ? tagFilters
        : [];

    setStatusFilter(nextStatus);
    setCategoryFilter(nextCategory);
    setSearch(nextSearch);
    setTagFilters(nextTags);

    // Active list key may still use debounced search/category for ~300ms — update it now.
    queryClient.setQueryData<ProtocolListResponse>(
      [...PROTOCOLS_QUERY_KEY, queryParams],
      (old) => upsertProtocolInListData(old, protocol),
    );
    // Also seed the key that will apply once debounce catches up.
    const nextParams = buildListParams(
      nextStatus,
      nextCategory,
      nextSearch,
      nextTags,
    );
    queryClient.setQueryData<ProtocolListResponse>(
      [...PROTOCOLS_QUERY_KEY, nextParams],
      (old) => upsertProtocolInListData(old, protocol),
    );
  }

  const uploadMutation = useMutation({
    mutationFn: (payload: ProtocolCreatePayload) =>
      protocolService.create(payload),
    onSuccess: (created) => {
      toast.success(t("upload.success"));
      setUploadOpen(false);
      setTitle("");
      setUploadCategory("");
      setUploadStatus(ProtocolStatus.ACTIVE);
      setUploadTagIds([]);
      setFile(null);
      applyFiltersToShowProtocol(created);
      syncProtocolInListCaches(queryClient, created);
      void queryClient.invalidateQueries({ queryKey: PROTOCOLS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PROTOCOL_TAGS_QUERY_KEY });
    },
    onError: (err) => toastApiError(err, "generic"),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, ...payload }: ProtocolUpdatePayload & { id: string }) =>
      protocolService.update(id, payload),
    onSuccess: (updated, variables) => {
      const markedObsolete =
        obsoleteTarget != null &&
        obsoleteTarget.id === variables.id &&
        variables.status === ProtocolStatus.OBSOLETE;
      toast.success(markedObsolete ? t("obsolete.success") : t("edit.success"));
      setEditTarget(null);
      setEditTitleError(null);
      setObsoleteTarget(null);
      setVersionsTarget((current) =>
        current != null && current.id === updated.id ? updated : current,
      );
      setPublishTarget((current) =>
        current != null && current.id === updated.id ? updated : current,
      );
      if (!markedObsolete) {
        applyFiltersToShowProtocol(updated);
      }
      syncProtocolInListCaches(queryClient, updated);
      void queryClient.invalidateQueries({ queryKey: PROTOCOLS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PROTOCOL_TAGS_QUERY_KEY });
    },
    onError: (err) => toastApiError(err, "generic"),
  });

  const publishMutation = useMutation({
    mutationFn: ({
      id,
      ...payload
    }: ProtocolPublishVersionPayload & { id: string }) =>
      protocolService.publishVersion(id, payload),
    onSuccess: (updated) => {
      toast.success(t("versions.success"));
      resetPublishForm();
      setPublishTarget(null);
      syncProtocolInListCaches(queryClient, updated);
      void queryClient.invalidateQueries({ queryKey: PROTOCOLS_QUERY_KEY });
    },
    onError: (err) => toastApiError(err, "generic"),
  });

  function resetPublishForm() {
    setVersionFile(null);
    setVersionBump(ProtocolVersionBump.MINOR);
    setChangeSummary("");
    setVersionFileError(null);
  }

  function openVersionsHistory(protocol: Protocol) {
    setVersionsTarget(protocol);
  }

  function openPublishVersion(protocol: Protocol) {
    resetPublishForm();
    setPublishTarget(protocol);
  }

  function validatePdf(next: File): boolean {
    const result = validateFile(next, PDF_VALIDATION);
    if (!result.ok) {
      if (result.error.code === "too_large") {
        toast.error(
          t("errors.fileTooLarge", {
            name: next.name,
            max: formatFileSizeLimit(result.error.maxSize),
          }),
        );
      } else {
        toast.error(t("errors.fileRequired"));
      }
      return false;
    }
    return true;
  }

  function selectPdfFile(
    files: File[],
    setSelected: (file: File | null) => void,
    setError: (error: string | null) => void,
  ) {
    const next = files[0];
    if (!next) return;
    if (!validatePdf(next)) return;
    setSelected(next);
    setError(null);
  }

  function submitUpload() {
    let ok = true;
    const trimmed = title.trim();
    if (!trimmed) {
      setTitleError(t("errors.titleRequired"));
      ok = false;
    } else {
      setTitleError(null);
    }
    if (!file) {
      setFileError(t("errors.fileRequired"));
      ok = false;
    } else {
      setFileError(null);
    }
    if (!ok || !file) return;
    const { tagIds, tagNames } = splitProtocolTagSelection(uploadTagIds);
    uploadMutation.mutate({
      title: trimmed,
      file,
      category: normalizeCategory(uploadCategory),
      status: uploadStatus,
      tag_ids: tagIds,
      tag_names: tagNames,
    });
  }

  function submitNewVersion() {
    if (!publishTarget) return;
    if (!versionFile) {
      setVersionFileError(t("errors.fileRequired"));
      return;
    }
    setVersionFileError(null);
    publishMutation.mutate({
      id: publishTarget.id,
      file: versionFile,
      change_summary: changeSummary.trim(),
      version_bump: versionBump,
    });
  }

  function closePreview() {
    setPreview(null);
  }

  async function openOrDownloadVersion(
    protocol: Protocol,
    version: ProtocolVersion,
    mode: "view" | "download",
  ) {
    setVersionBusyId(`${version.id}:${mode}`);
    try {
      const blob = await protocolService.downloadVersionFile(
        protocol.id,
        version.id,
      );
      if (mode === "download") {
        downloadBlob(blob, version.original_filename);
        return;
      }
      const url = URL.createObjectURL(blob);
      setPreview({
        title: `${protocol.title} (v${version.version})`,
        url,
      });
    } catch (err) {
      toastApiError(err, "generic");
    } finally {
      setVersionBusyId(null);
    }
  }

  return (
    <PageFrame
      isLoading={isLoading && data == null}
      error={error != null && data == null}
      errorMessage={t("errors.loadError")}
      onRetry={() => void refetch()}
    >
      <div className={pageLayoutClass}>
        <PageHeader
          title={t("title")}
          description={t("subtitle")}
          icon={FileText}
          actions={
            <>
              <a
                href={PROTOCOL_TEMPLATE_HREF}
                download="protocol-template.md"
                className={buttonVariants({ variant: "outline" })}
              >
                <FileDown className="h-4 w-4" />
                {t("template.download")}
              </a>
              {canWrite ? (
                <Button
                  type="button"
                  leftIcon={<Upload className="h-4 w-4" />}
                  onClick={() => setUploadOpen(true)}
                >
                  {t("upload.title")}
                </Button>
              ) : null}
            </>
          }
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={t("filters.searchPlaceholder")}
            searchLabel={tCommon("search")}
            clearSearchLabel={tCommon("clearSearch")}
            className="min-w-0 max-w-none sm:min-w-[240px] sm:flex-1 lg:max-w-sm"
          />
          <div className="w-full sm:w-48">
            <Input
              id="protocol-filter-category"
              aria-label={t("filters.categoryLabel")}
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              placeholder={t("filters.categoryPlaceholder")}
            />
          </div>
          <FilterBar>
            <AdminFilterMenu
              name={t("filters.statusLabel")}
              value={statusFilter}
              defaultValue="all"
              onChange={setStatusFilter}
              options={[
                { value: "all" as const, label: t("filters.statusAll") },
                ...PROTOCOL_STATUSES.map((status) => ({
                  value: status,
                  label: t(`status.${status}`),
                  dotClassName: STATUS_FILTER_DOT_CLASS[status],
                })),
              ]}
            />
            <AdminFilterMenu
              mode="multiple"
              icon={Tags}
              name={t("filters.tagLabel")}
              value={tagFilters}
              onChange={setTagFilters}
              anyLabel={t("filters.tagAll")}
              countLabel={(count) => t("filters.tagCount", { count })}
              options={tagItems.map((tag) => ({
                value: tag.id,
                label: tag.name,
              }))}
            />
          </FilterBar>
        </div>

        {items.length === 0 ? (
          <EmptyState
            variant="dashed"
            icon={FileText}
            title={emptyMessage}
            action={
              canWrite ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  leftIcon={<Upload className="h-4 w-4" />}
                  onClick={() => setUploadOpen(true)}
                >
                  {t("upload.title")}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul
            className={cn(
              cardShellClass,
              "animate-slide-up divide-surface-200/70 divide-y overflow-hidden",
            )}
          >
            {items.map((protocol) => (
              <li
                key={protocol.id}
                className="hover:bg-surface-50 dark:hover:bg-surface-100/50 flex flex-col gap-3 px-5 py-4 transition-colors sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3.5">
                  <span
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
                      protocol.status === ProtocolStatus.OBSOLETE
                        ? "bg-surface-100 text-surface-400 ring-surface-500/15"
                        : "bg-error-50 text-error-600 ring-error-600/15",
                    )}
                    aria-hidden
                  >
                    <FileText className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-surface-900 truncate text-sm font-semibold tracking-tight">
                        {protocol.title}
                      </p>
                      <Badge
                        variant={STATUS_BADGE_VARIANT[protocol.status]}
                        size="sm"
                      >
                        {t(`status.${protocol.status}`)}
                      </Badge>
                      <Badge
                        variant="outline"
                        size="sm"
                      >
                        v{protocol.current_version.version}
                      </Badge>
                      {protocol.category != null ? (
                        <Badge
                          variant="outline"
                          size="sm"
                        >
                          {protocol.category}
                        </Badge>
                      ) : null}
                      {protocol.tags.map((tag) => (
                        <Badge
                          key={tag.id}
                          variant="outline"
                          size="sm"
                        >
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-surface-500 mt-1 truncate text-[13px]">
                      {protocol.current_version.original_filename}
                      {" · "}
                      {formatUploadedAt(
                        protocol.current_version.created_at,
                        i18n.language,
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:pl-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Eye className="h-4 w-4" />}
                    isLoading={
                      versionBusyId === `${protocol.current_version.id}:view`
                    }
                    onClick={() =>
                      void openOrDownloadVersion(
                        protocol,
                        protocol.current_version,
                        "view",
                      )
                    }
                  >
                    {t("actions.view")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    isLoading={
                      versionBusyId ===
                      `${protocol.current_version.id}:download`
                    }
                    onClick={() =>
                      void openOrDownloadVersion(
                        protocol,
                        protocol.current_version,
                        "download",
                      )
                    }
                  >
                    {t("actions.download")}
                  </Button>
                  <ActionMenu
                    renderTrigger={(triggerProps) => (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        aria-label={t("actions.more")}
                        {...triggerProps}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    )}
                  >
                    {canWrite && protocol.status === ProtocolStatus.ACTIVE ? (
                      <ActionMenuItem
                        icon={Upload}
                        onSelect={() => openPublishVersion(protocol)}
                      >
                        {t("actions.addVersion")}
                      </ActionMenuItem>
                    ) : null}
                    <ActionMenuItem
                      icon={History}
                      onSelect={() => openVersionsHistory(protocol)}
                    >
                      {t("actions.versions")}
                    </ActionMenuItem>
                    {canWrite ? (
                      <>
                        <ActionMenuItem
                          icon={Pencil}
                          onSelect={() => {
                            setEditTarget(protocol);
                            setEditTitle(protocol.title);
                            setEditCategory(protocol.category ?? "");
                            setEditStatus(protocol.status);
                            setEditTagIds(protocol.tags.map((tag) => tag.id));
                            setEditTitleError(null);
                          }}
                        >
                          {t("actions.edit")}
                        </ActionMenuItem>
                        {protocol.status !== ProtocolStatus.OBSOLETE ? (
                          <ActionMenuItem
                            icon={Archive}
                            onSelect={() => setObsoleteTarget(protocol)}
                          >
                            {t("actions.markObsolete")}
                          </ActionMenuItem>
                        ) : null}
                      </>
                    ) : null}
                  </ActionMenu>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal
        isOpen={preview != null}
        onClose={closePreview}
        title={preview?.title ?? t("actions.view")}
        closeLabel={tCommon("close")}
        size="xl"
      >
        {preview ? (
          <iframe
            title={preview.title}
            src={preview.url}
            className="border-surface-200 bg-surface-50 h-[70vh] w-full rounded-lg border"
          />
        ) : null}
      </Modal>

      <Modal
        isOpen={uploadOpen}
        onClose={() => {
          if (!uploadMutation.isPending) setUploadOpen(false);
        }}
        title={t("upload.title")}
        closeLabel={tCommon("close")}
        size="lg"
      >
        <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="protocol-title">
              {t("upload.titleLabel")}
            </Label>
            <Input
              id="protocol-title"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                if (titleError) setTitleError(null);
              }}
              placeholder={t("upload.titlePlaceholder")}
              aria-invalid={titleError != null}
              aria-describedby={
                titleError ? fieldErrorId("protocol-title") : undefined
              }
            />
            {titleError ? (
              <FieldError id={fieldErrorId("protocol-title")}>
                {titleError}
              </FieldError>
            ) : null}
          </div>
          <ProtocolMetadataFields
            categoryId="protocol-category"
            category={uploadCategory}
            onCategoryChange={setUploadCategory}
            status={uploadStatus}
            onStatusChange={setUploadStatus}
            statusOptions={statusOptions}
            categoryLabel={t("upload.categoryLabel")}
            categoryPlaceholder={t("upload.categoryPlaceholder")}
            statusLabel={t("upload.statusLabel")}
          />
          <ProtocolPdfField
            id="protocol-pdf"
            file={file}
            fileError={fileError}
            onFilesSelected={(files) =>
              selectPdfFile(files, setFile, setFileError)
            }
            fileLabel={t("upload.fileLabel")}
            dropzoneTitle={t("upload.dropzoneTitle")}
            dropzoneDescription={t("upload.dropzoneDescription")}
          />
          <div className="sm:col-span-2">
            <ProtocolTagsField
              id="protocol-upload-tags"
              options={tagOptions}
              value={uploadTagIds}
              onChange={setUploadTagIds}
              label={t("tags.label")}
              placeholderLabel={t("tags.placeholder")}
              searchPlaceholderLabel={t("tags.searchPlaceholder")}
              createOptionLabel={(name) => t("tags.create", { name })}
              removeChipLabel={(chipLabel) =>
                tComponents("multiSelectRemoveChip", {
                  label: chipLabel,
                })
              }
            />
          </div>
        </div>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setUploadOpen(false)}
              disabled={uploadMutation.isPending}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              leftIcon={<Upload className="h-4 w-4" />}
              isLoading={uploadMutation.isPending}
              onClick={submitUpload}
            >
              {t("upload.submit")}
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      <Modal
        isOpen={editTarget != null}
        onClose={() => {
          if (!editMutation.isPending) {
            setEditTarget(null);
            setEditTitleError(null);
          }
        }}
        title={t("edit.title")}
        closeLabel={tCommon("close")}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="protocol-edit-title">
              {t("upload.titleLabel")}
            </Label>
            <Input
              id="protocol-edit-title"
              value={editTitle}
              onChange={(event) => {
                setEditTitle(event.target.value);
                if (editTitleError) setEditTitleError(null);
              }}
              aria-invalid={editTitleError != null}
              aria-describedby={
                editTitleError ? fieldErrorId("protocol-edit-title") : undefined
              }
            />
            {editTitleError ? (
              <FieldError id={fieldErrorId("protocol-edit-title")}>
                {editTitleError}
              </FieldError>
            ) : null}
          </div>
          <ProtocolMetadataFields
            categoryId="protocol-edit-category"
            category={editCategory}
            onCategoryChange={setEditCategory}
            status={editStatus}
            onStatusChange={setEditStatus}
            statusOptions={statusOptions}
            categoryLabel={t("upload.categoryLabel")}
            categoryPlaceholder={t("upload.categoryPlaceholder")}
            statusLabel={t("upload.statusLabel")}
          />
          <ProtocolTagsField
            id="protocol-edit-tags"
            options={tagOptions}
            value={editTagIds}
            onChange={setEditTagIds}
            label={t("tags.label")}
            placeholderLabel={t("tags.placeholder")}
            searchPlaceholderLabel={t("tags.searchPlaceholder")}
            createOptionLabel={(name) => t("tags.create", { name })}
            removeChipLabel={(chipLabel) =>
              tComponents("multiSelectRemoveChip", { label: chipLabel })
            }
          />
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditTarget(null);
                setEditTitleError(null);
              }}
              disabled={editMutation.isPending}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              isLoading={editMutation.isPending}
              onClick={() => {
                if (!editTarget) return;
                const nextTitle = editTitle.trim();
                if (!nextTitle) {
                  setEditTitleError(t("errors.titleRequired"));
                  return;
                }
                const { tagIds, tagNames } =
                  splitProtocolTagSelection(editTagIds);
                editMutation.mutate({
                  id: editTarget.id,
                  title: nextTitle,
                  category: normalizeCategory(editCategory),
                  status: editStatus,
                  tag_ids: tagIds,
                  tag_names: tagNames,
                });
              }}
            >
              {t("edit.submit")}
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      <Modal
        isOpen={versionsTarget != null}
        onClose={() => setVersionsTarget(null)}
        title={t("versions.title", { title: versionsTarget?.title ?? "" })}
        closeLabel={tCommon("close")}
        size="lg"
      >
        <div className="space-y-4">
          {canWrite && versionsTarget?.status === ProtocolStatus.ACTIVE ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Upload className="h-4 w-4" />}
                onClick={() => {
                  if (!versionsTarget) return;
                  const protocol = versionsTarget;
                  setVersionsTarget(null);
                  openPublishVersion(protocol);
                }}
              >
                {t("actions.addVersion")}
              </Button>
            </div>
          ) : null}
          {canWrite &&
          versionsTarget != null &&
          versionsTarget.status !== ProtocolStatus.ACTIVE ? (
            <p className="text-surface-500 text-sm">
              {t("versions.publishBlocked")}
            </p>
          ) : null}
          {versionsQuery.isLoading ? (
            <p className="text-surface-500 text-sm">{tCommon("loading")}</p>
          ) : versionsQuery.error != null ? (
            <p className="text-error-600 text-sm">{t("versions.loadError")}</p>
          ) : (
            <ul className="divide-surface-100 border-surface-200 divide-y rounded-lg border">
              {versionItems.map((version) => (
                <li
                  key={version.id}
                  className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-surface-900 font-medium">
                        v{version.version}
                      </p>
                      {version.is_current ? (
                        <Badge
                          variant="success"
                          size="sm"
                        >
                          {t("versions.current")}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-surface-500 mt-0.5 text-sm">
                      {version.change_summary.trim() !== ""
                        ? `${version.change_summary} · `
                        : null}
                      {formatUploadedAt(version.created_at, i18n.language)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      leftIcon={<Eye className="h-4 w-4" />}
                      isLoading={versionBusyId === `${version.id}:view`}
                      onClick={() => {
                        if (!versionsTarget) return;
                        void openOrDownloadVersion(
                          versionsTarget,
                          version,
                          "view",
                        );
                      }}
                    >
                      {t("actions.view")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      leftIcon={<Download className="h-4 w-4" />}
                      isLoading={versionBusyId === `${version.id}:download`}
                      onClick={() => {
                        if (!versionsTarget) return;
                        void openOrDownloadVersion(
                          versionsTarget,
                          version,
                          "download",
                        );
                      }}
                    >
                      {t("actions.download")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={publishTarget != null}
        onClose={() => {
          if (!publishMutation.isPending) {
            setPublishTarget(null);
            resetPublishForm();
          }
        }}
        title={t("versions.publishTitle", {
          title: publishTarget?.title ?? "",
        })}
        closeLabel={tCommon("close")}
        size="md"
      >
        {publishTarget != null &&
        publishTarget.status !== ProtocolStatus.ACTIVE ? (
          <p className="text-surface-500 text-sm">
            {t("versions.publishBlocked")}
          </p>
        ) : (
          <div className="space-y-4">
            {publishTarget != null ? (
              <div className="space-y-2">
                <Label
                  as="p"
                  spacing="field"
                >
                  {t("versions.bumpLabel")}
                </Label>
                <div
                  role="radiogroup"
                  aria-label={t("versions.bumpLabel")}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  {(
                    [
                      ProtocolVersionBump.MINOR,
                      ProtocolVersionBump.MAJOR,
                    ] as const
                  ).map((bump) => {
                    const selected = versionBump === bump;
                    const to = nextProtocolVersionLabel(
                      publishTarget.current_version.version,
                      bump,
                    );
                    const isMinor = bump === ProtocolVersionBump.MINOR;
                    return (
                      <button
                        key={bump}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setVersionBump(bump)}
                        className={cn(
                          "rounded-xl border p-4 text-left transition-colors",
                          selected
                            ? "border-primary-500 bg-primary-50 ring-primary-500 ring-1"
                            : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:bg-surface-50",
                        )}
                      >
                        <p className="text-surface-900 font-medium">
                          {isMinor
                            ? t("versions.bumpMinor")
                            : t("versions.bumpMajor")}
                          <span className="text-surface-500 ml-1.5 font-normal tabular-nums">
                            v{to}
                          </span>
                        </p>
                        <p className="text-surface-500 mt-1 text-sm">
                          {isMinor
                            ? t("versions.bumpMinorDescription")
                            : t("versions.bumpMajorDescription")}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <Textarea
              id="protocol-change-summary"
              label={t("versions.changeSummaryLabel")}
              value={changeSummary}
              onChange={(event) => setChangeSummary(event.target.value)}
              placeholder={t("versions.changeSummaryPlaceholder")}
              hint={t("versions.changeSummaryHint")}
              rows={4}
              className="min-h-[6.5rem] resize-y"
              maxLength={500}
            />
            <ProtocolPdfField
              id="protocol-version-pdf"
              file={versionFile}
              fileError={versionFileError}
              onFilesSelected={(files) =>
                selectPdfFile(files, setVersionFile, setVersionFileError)
              }
              fileLabel={t("upload.fileLabel")}
              dropzoneTitle={t("upload.dropzoneTitle")}
              dropzoneDescription={t("upload.dropzoneDescription")}
            />
            <ModalFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (!publishMutation.isPending) {
                    setPublishTarget(null);
                    resetPublishForm();
                  }
                }}
              >
                {tCommon("cancel")}
              </Button>
              <Button
                type="button"
                leftIcon={<Upload className="h-4 w-4" />}
                isLoading={publishMutation.isPending}
                onClick={submitNewVersion}
              >
                {t("versions.submit")}
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={obsoleteTarget != null}
        onClose={() => {
          if (!editMutation.isPending) setObsoleteTarget(null);
        }}
        onConfirm={() => {
          if (!obsoleteTarget) return;
          editMutation.mutate({
            id: obsoleteTarget.id,
            title: obsoleteTarget.title,
            category: obsoleteTarget.category,
            status: ProtocolStatus.OBSOLETE,
            tag_ids: obsoleteTarget.tags.map((tag) => tag.id),
            tag_names: [],
          });
        }}
        title={t("obsolete.title")}
        message={t("obsolete.body", {
          title: obsoleteTarget?.title ?? "",
        })}
        confirmText={t("obsolete.confirm")}
        isDestructive
        isLoading={editMutation.isPending && obsoleteTarget != null}
      />
    </PageFrame>
  );
}
